import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import _uniqueId from 'lodash/uniqueId';
import ActionList from 'src/app/components/ActionList';
import { useColumnDefs, useFuseOptions } from "src/app/slicedForm/shared/context/ColumnDefsProvider";
import useKeyboardMenuNavigation from 'src/app/components/SearchableMultiSelect/useKeyboardMenuNavigation';
import useMetricFilter from "src/hooks/useFilterFormMetrics";
import { useFormSelector } from "src/app/slicedForm/shared/context/FormProvider";
import { selectColumnsAsList } from "src/app/slicedForm/ColumnForm/reducers/columnReducer";
import FormModal from 'src/app/components/FormModal';
import MetricSearchInput from 'src/app/slicedForm/shared/elements/MetricSearchInput';
import ProfilesSection from 'src/app/slicedForm/shared/elements/ProfilesSection';
import ColumnGroupSelector from "src/app/slicedForm/shared/elements/ColumnGroupSelector";
import PredefinedProfileEditBlocker from 'src/app/slicedForm/shared/elements/PredefinedProfileEditBlocker';
import HoverFieldDefinition from 'src/app/slicedForm/ColumnForm/elements/HoverFieldDefinition';
import {
  Box,
  makeStyles,
} from '@material-ui/core';
import PropTypes from "prop-types";
import { useProfileLocked } from '../../shared/context/ProfileLockedProvider';
import ConditionalWrapper from 'src/app/components/utility/ConditionalWrapper';


const useStyles = makeStyles((theme) => ({
  relative: {
    position: 'relative'
  },
  drawerCol: {
    flex: 1,
    borderRight: `1px solid ${theme.palette.background.panelBorder}`,
    height: '100%',
  },
}));


export function CheckboxRow({ index, style, data }) {
  const colDef = data.filteredColumns[index];
  const selected = data.selectedColumns.includes(colDef.name)

  return (
    <ActionList.Item
      pl={6}
      style={style}
      selected={selected}
      checkbox
      selectionVariant='multiple'
      role="menuitemcheckbox"
      onSelect={() => data.onSelect(colDef.name, selected)}
      title={colDef.label}
      {...data.getOptionProps(index, { selected })}
    >
      {colDef.label}
      <ActionList.Trailing>
        <HoverFieldDefinition
          colDef={colDef}
          anchorPosition="right"
        />
      </ActionList.Trailing>
    </ActionList.Item>
  )
}


function ColumnSelectionInner({
  leftColumnClassName,
  rightColumnClassName,
  searchInputClassName,
  showProfileControls,
  leftColumnIsDrawer,
  leftColumnWidth,
  onSelect,
  autoFocusSearch,
  RowComponent,
}) {
  const classes = useStyles();
  const [id] = useState(() => _uniqueId('column-selection-'));
  const columnDefs = useColumnDefs();
  const [query, setQuery] = useState('');
  const { locked } = useProfileLocked();
  const selectedColumns = useFormSelector(selectColumnsAsList);
  const fuseOptions = useFuseOptions();
  const [selectedGroup, setSelectedGroup] = useState('ALL');

  const filteredColumns = useMetricFilter({
    metrics: columnDefs,
    query,
    selectedGroup,
    fuseOptions
  });

  const {
    resetHighlighted,
    getRootProps,
    getInputProps,
    getListboxProps,
    getOptionProps
  } = useKeyboardMenuNavigation(
    id,
    filteredColumns,
    false,
    {
      onEnter: (item) => {
        const selected = selectedColumns.includes(item.name);
        onSelect(item.name, selected);
      },
      initialIndex: 0,
      listBoxRoleSelector: 'div'
    }
  );

  const itemKey = useCallback((index) => filteredColumns[index].name, [filteredColumns]);


  const handleSearchChange = useCallback((event) => {
    setQuery(event.target.value || '')
  }, [])


  useEffect(() => {
    resetHighlighted();
  }, [query])


  return (
    <>
      <ConditionalWrapper
        condition={leftColumnIsDrawer}
        wrapper={(children) => {
          return (
            <FormModal.Drawer
              className={leftColumnClassName}
              width={leftColumnWidth}
              breakpoint="md"
              zIndex={3}
            >
              {children}
            </FormModal.Drawer>
          )
        }}
      >
        <FormModal.Column
          className={clsx(leftColumnClassName, classes.drawerCol)}
          minWidth={leftColumnWidth}
          maxWidth={leftColumnWidth}
        >
          {showProfileControls && (
            <FormModal.Section
              title="Saved Profiles"
              expand
              scrollable
              borderBottom={true}
            >
              <ProfilesSection />
            </FormModal.Section>
          )}
          <FormModal.Section
            title="Column Groups"
            scrollable
            flexShrink={3}
          >
            <ColumnGroupSelector
              selected={selectedGroup}
              includeAllCategory
              onSelect={setSelectedGroup}
            />
          </FormModal.Section>
        </FormModal.Column>
      </ConditionalWrapper>
      <FormModal.Column
        className={clsx(rightColumnClassName, classes.relative)}
        borderLeft={false}
        borderRight
        expand
        {...getRootProps()}
      >
        <PredefinedProfileEditBlocker disablePortal={false} />
        <FormModal.Section
          className={searchInputClassName}
          borderBottom
        >
          <Box p={1.5}>
            <MetricSearchInput
              placeholder="Search metrics"
              value={query}
              autoFocus={autoFocusSearch}
              onChange={handleSearchChange}
              disabled={locked}
              ariaLabel="Search metrics. Use Up, Down, and Enter arrow keys for navigation."
              {...getInputProps('inputRef')}
            />
          </Box>
        </FormModal.Section>
        <FormModal.Section expand>
          <ActionList.Virtual
            className={'modal-horizontal-scrollbar-sm'}
            itemHeight={35}
            itemCount={filteredColumns.length}
            itemKeyCallback={itemKey}
            disabled={locked}
            itemData={{
              filteredColumns,
              selectedColumns,
              onSelect,
              getOptionProps,
            }}
            {...getListboxProps('innerRef')}
          >
            {RowComponent}
          </ActionList.Virtual>
        </FormModal.Section>
      </FormModal.Column>
    </>
  )
}


ColumnSelectionInner.propTypes = {
  showProfileControls: PropTypes.bool,
  selectedColumns: PropTypes.array,
  autoFocusSearch: PropTypes.bool,
  onSelect: PropTypes.func,
  leftColumnIsDrawer: PropTypes.bool,
}


ColumnSelectionInner.defaultProps = {
  selectedColumns: [],
  leftColumnWidth: 260,
  leftColumnIsDrawer: true,
  RowComponent: CheckboxRow
}


export default ColumnSelectionInner;
